import { useParams, Link } from "react-router-dom";
import SidebarLayout from "@/components/feature/SidebarLayout";
import { useScrollReveal, getRevealClass } from "@/hooks/useScrollReveal";
import { newsDetail, newsList, type ArticleDetail } from "@/mocks/news";

function ArticleBlocks({ article }: { article: ArticleDetail }) {
  return (
    <div className="flex flex-col">
      {article.blocks.map((block, i) => {
        if (block.type === "subheading") {
          return (
            <h3
              key={i}
              className="text-xl md:text-2xl font-bold text-black tracking-tight mt-8 mb-5 leading-snug"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              {block.text}
            </h3>
          );
        }
        if (block.type === "paragraph") {
          return (
            <p
              key={i}
              className="text-base text-gray-700 leading-[2.2] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              {block.text}
            </p>
          );
        }
        if (block.type === "image") {
          return (
            <figure key={i} className="my-8">
              <img
                src={block.src}
                alt={block.alt || ""}
                className="w-full max-w-[400px] mx-auto h-auto object-cover"
              />
            </figure>
          );
        }
        if (block.type === "list") {
          return (
            <ul key={i} className="flex flex-col gap-2 mb-5">
              {block.items?.map((item, j) => (
                <li
                  key={j}
                  className="text-lg font-bold text-gray-700 leading-[2.2]"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  • {item}
                </li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </div>
  );
}

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? newsDetail[id] : null;
  const { ref, isVisible } = useScrollReveal();

  if (!article) {
    return (
      <SidebarLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <p className="text-gray-400 text-sm mb-4">記事が見つかりませんでした</p>
          <Link to="/news" className="text-xs tracking-widest border-b border-black pb-0.5 cursor-pointer whitespace-nowrap">
            ニュース一覧へ戻る
          </Link>
        </div>
      </SidebarLayout>
    );
  }

  const currentIndex = newsList.findIndex((n) => n.id === id);
  const prevArticle = currentIndex > 0 ? newsList[currentIndex - 1] : null;
  const nextArticle = currentIndex < newsList.length - 1 ? newsList[currentIndex + 1] : null;

  return (
    <SidebarLayout breadcrumbs={[
      { label: 'ニュース', path: '/news' },
      { label: article.title },
    ]}>

      {/* Main Content Card */}
      <div className="px-4 md:px-6 lg:px-10 pb-10">
        <div className="bg-white rounded-[20px] overflow-hidden">
          <div className="p-8 lg:p-12">
            {/* Date & Title */}
            <div ref={ref} className={getRevealClass(isVisible)}>
              <div className="mb-6">
                <p className="text-xs text-gray-400 tracking-wider mb-3">{article.date}</p>
                <h1
                  className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-snug"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {article.title}
                </h1>
              </div>
            </div>

            {/* Main Image */}
            <div className="w-full overflow-hidden rounded-lg mb-10">
              <img
                src={article.image}
                alt={article.title}
                className={`w-full h-auto max-h-[460px] object-cover ${article.id === "006" || article.id === "008" ? "object-bottom" : article.id === "015" ? "object-top" : "object-center"}`}
              />
            </div>

            {/* Content */}
            <div className="max-w-3xl mx-auto">
              <ArticleBlocks article={article} />
            </div>
          </div>

          {/* Article Navigation */}
          <div className="px-8 lg:px-12 pb-10">
            <div className="border-t border-gray-100 pt-8 flex flex-col gap-4">
              {prevArticle && (
                <Link
                  to={`/news/${prevArticle.id}`}
                  className="flex items-center gap-4 group cursor-pointer bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400 group-hover:text-black transition-colors shrink-0">
                    <i className="ri-arrow-left-line" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-400 mb-0.5 whitespace-nowrap">{prevArticle.date}</p>
                    <p className="text-sm font-medium text-black truncate">{prevArticle.title}</p>
                  </div>
                  <div className="w-16 h-10 shrink-0 overflow-hidden rounded-lg">
                    <img
                      src={prevArticle.image}
                      alt={prevArticle.title}
                      className={`w-full h-full object-cover ${prevArticle.id === "006" || prevArticle.id === "008" ? "object-bottom" : "object-top"}`}
                    />
                  </div>
                </Link>
              )}
              {nextArticle && (
                <Link
                  to={`/news/${nextArticle.id}`}
                  className="flex items-center gap-4 group cursor-pointer bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="w-16 h-10 shrink-0 overflow-hidden rounded-lg">
                    <img
                      src={nextArticle.image}
                      alt={nextArticle.title}
                      className={`w-full h-full object-cover ${nextArticle.id === "006" || nextArticle.id === "008" ? "object-bottom" : "object-top"}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-400 mb-0.5 whitespace-nowrap">{nextArticle.date}</p>
                    <p className="text-sm font-medium text-black truncate">{nextArticle.title}</p>
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400 group-hover:text-black transition-colors shrink-0">
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="px-4 md:px-6 lg:px-10 pb-10">
        <div className="bg-[#025ab0] rounded-[20px] overflow-hidden">
          <div className="p-8 lg:p-10">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-1">Contact</h2>
                <p className="text-sm text-white/70 mb-4">お問い合わせ</p>
                <div className="w-8 h-px bg-white mb-5" />
                <p className="text-sm text-white/80 leading-relaxed max-w-md">
                  お客様からのお問い合わせをお待ちしております。<br />
                  お気軽にご連絡ください。
                </p>
              </div>
              <Link
                to="/contact"
                className="shrink-0 inline-flex items-center gap-3 border border-white text-white text-xs tracking-widest px-10 py-4 hover:bg-white hover:text-[#025ab0] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
}